import { YES_NO_OPTIONS } from '@/contants/common';
import { useGetDefaultCustomerSettings } from '@/redux/hooks/useCustomerSettings';
import { hasDuplicates } from '@/utils/array';
import FormInputNumber from '@/views/components/Inputs/FormInputNumber';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { Form } from 'antd';
import { uniq } from 'es-toolkit';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  SHIPMENT_STATUS_ALLOWED_STATUS_FOR_CONFIRMED,
  SHIPMENT_STATUS_ALLOWED_STATUS_FOR_RATE_ERROR,
  SHIPMENT_STATUS_FOR_REASONS,
  SHIPMENT_STATUS_VALIDATION_FOR_AUTO_ROUTE,
  SHIPMENT_STATUS_VALIDATION_FOR_CANCELLED,
  SHIPMENT_STATUS_VALIDATION_FOR_CLOSED,
  SHIPMENT_STATUS_VALIDATION_FOR_HOLD,
  SHIPMENT_STATUS_VALIDATION_FOR_NEW_AND_PLANNING,
} from '../../../../../contants/Fields';
import { setAppLoader } from '../../../../../redux/common/CommonActions';
import {
  getShipmentListInProgress,
  setSelectedShipments,
  setShipmentList,
} from '../../../../../redux/shipment/ShipmentActions';
import { updateShipmentStatusEffect } from '../../../../../redux/shipment/ShipmentEffects';
import { routeFileSelector } from '../../../../../utilz/i18n';
import HiddenFormInput from '../../../../components/Inputs/HiddenFormInput';
import PrimaryLabel from '../../../../components/Labels/PrimaryLabel';
import ConfirmationModal from '../../../../components/Modals/ConfirmationModal';
import WarningModal from '../../../../components/Modals/WarningModal';
import ShipmentStatusSelector from '../../../../components/Selectors/ShipmentStatusSelector';
import SimpleTable from '../../../../components/Tables/SimpleTable';
import { SHIPMENT_STATUS } from '../../constants';
import { QUOTE_STATUSES } from '../QuoteCard/constants';
import { ShipmentStatusReasonSelect } from '../selects';

const ShipmentStatusUpdateForm = ({ parentFormRef, handleUpdateStatus = () => {}, selectedShipments = [] }) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [toDeliveredModalVisible, setToDeliveredModalVisible] = useState(false);
  const [alertModalVisible, setAlertModalVisible] = useState(false);
  const [toAcknowledgedModalVisible, setToAcknowledgedModalVisible] = useState(false);
  const [isCancelledCostModalVisible, setIsCancelledCostModalVisible] = useState(false);
  const [alertModalDescription, setAlertModalDescription] = useState('');
  const [form] = parentFormRef ? [parentFormRef] : Form.useForm;
  const { selectedCompany } = useSelector((state) => state.auth);
  const [isCancelledCostVisible, setIsCancelledCostVisible] = useState(false);

  const { data: defaultSettings } = useGetDefaultCustomerSettings(
    selectedCompany.companyId || 0,
    'Shipment',
    'ShipmentCancellationCost',
  );

  const isShipmentCancellationCost = defaultSettings?.[0]?.defaultValue === YES_NO_OPTIONS?.[0]?.value;

  const dispatch = useDispatch();
  const onSubmit = (data) => {
    if (data.shipmentStatus === 'Closed') {
      const hasDuplicate = [
        ...uniq(selectedShipments.map((shipment) => shipment.fromShipmentStatus)),
        ...SHIPMENT_STATUS_VALIDATION_FOR_CLOSED,
      ];
      if (hasDuplicates(hasDuplicate)) {
        setToDeliveredModalVisible(true);
        return false;
      }
    } else if (data.shipmentStatus === 'Hold') {
      const hasDuplicate = [
        ...uniq(selectedShipments.map((shipment) => shipment.fromShipmentStatus)),
        ...SHIPMENT_STATUS_VALIDATION_FOR_HOLD,
      ];
      if (hasDuplicates(hasDuplicate)) {
        setAlertModalDescription(
          t('translation:shipmentAdd.vendorDetails.alertModalDescription.unableToUpdateShipmentNew'),
        );
        setAlertModalVisible(true);
        return false;
      }
    } else if (data.shipmentStatus === 'New') {
      const hasDuplicate = [
        ...uniq(selectedShipments.map((shipment) => shipment.fromShipmentStatus)),
        ...SHIPMENT_STATUS_VALIDATION_FOR_NEW_AND_PLANNING,
      ];
      if (hasDuplicates(hasDuplicate)) {
        setAlertModalDescription(t('translation:shipmentAdd.vendorDetails.alertModalDescription.shipmentNoUpdatedNew'));
        setAlertModalVisible(true);
        return false;
      }
    } else if (data.shipmentStatus === 'Planning') {
      const hasDuplicate = [
        ...uniq(selectedShipments.map((shipment) => shipment.fromShipmentStatus)),
        ...SHIPMENT_STATUS_VALIDATION_FOR_NEW_AND_PLANNING,
      ];
      if (hasDuplicates(hasDuplicate)) {
        setAlertModalDescription(
          t('translation:shipmentAdd.vendorDetails.alertModalDescription.shipmentNoUpdatedPlanning'),
        );
        setAlertModalVisible(true);
        return false;
      }
    } else if (data.shipmentStatus === 'Confirmed') {
      setToAcknowledgedModalVisible(true);
      return false;
    } else if (data.shipmentStatus === 'Auto-Route') {
      const hasDuplicate = [
        ...uniq(selectedShipments.map((shipment) => shipment.fromShipmentStatus)),
        ...SHIPMENT_STATUS_VALIDATION_FOR_AUTO_ROUTE,
      ];
      if (hasDuplicates(hasDuplicate)) {
        setAlertModalDescription(
          t('translation:shipmentAdd.vendorDetails.alertModalDescription.unableToUpdateShipmentAutoRoute'),
        );
        setAlertModalVisible(true);
        return false;
      }
    } else if (data.shipmentStatus === 'Rate Error') {
      const hasDuplicate = [
        ...uniq(selectedShipments.map((shipment) => shipment.fromShipmentStatus)),
        ...SHIPMENT_STATUS_ALLOWED_STATUS_FOR_RATE_ERROR,
      ];

      if (uniq(hasDuplicate).length > SHIPMENT_STATUS_ALLOWED_STATUS_FOR_RATE_ERROR.length) {
        setAlertModalDescription(
          t('translation:shipmentAdd.vendorDetails.alertModalDescription.unableToUpdateShipmentRateError'),
        );
        setAlertModalVisible(true);
        return false;
      }
    } else if (data.shipmentStatus === QUOTE_STATUSES.cancelled) {
      const hasDuplicate = [
        ...uniq(selectedShipments.map((shipment) => shipment.fromShipmentStatus)),
        ...SHIPMENT_STATUS_VALIDATION_FOR_CANCELLED,
      ];
      if (!hasDuplicates(hasDuplicate)) {
        setAlertModalDescription(
          t('translation:shipmentAdd.vendorDetails.alertModalDescription.shipmentStatusAutoTendered'),
        );
        setAlertModalVisible(true);
        return false;
      }
    }
    handleModalResponse(0);
  };

  const handleModalResponse = (response) => {
    const formData = form.getFieldsValue();
    setToDeliveredModalVisible(false);
    const selectedShipmentsLocal = selectedShipments.map((row) => ({
      ...row,
      isDelivered: response ? 1 : 0,
      ...formData,
    }));
    handleApiUpdate(selectedShipmentsLocal);
  };

  const handleApiUpdate = async (shipments) => {
    const feedbacksShipmentArrays = [];
    dispatch(setAppLoader(true));

    for await (const row of shipments) {
      try {
        const { data } = await updateShipmentStatusEffect(row);
        if (data === 0) {
          feedbacksShipmentArrays.push({
            shipmentNo: row.shipmentNumber,
            status: false,
            message: t('translation:default.notification.failed'),
          });
        } else if (data === 1) {
          feedbacksShipmentArrays.push({
            shipmentNo: row.shipmentNumber,
            status: true,
            message: t('translation:default.notification.success'),
          });
        }
      } catch (error) {
        const errorMessage = error?.response?.data;
        feedbacksShipmentArrays.push({
          shipmentNo: row.shipmentNumber,
          status: false,
          message: errorMessage ? errorMessage : t('translation:default.notification.failed'),
        });
      }
    }

    const allItemsSucceeded = feedbacksShipmentArrays.every((item) => item.status);

    if (allItemsSucceeded) {
      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        message: 'Successfully updated shipment status.',
      });
      handleUpdateStatus(true);
      dispatch(setSelectedShipments([]));
      dispatch(setShipmentList({ data: [], progressing: true }));
      dispatch(getShipmentListInProgress());
    } else {
      setAlertModalDescription(GenerateError(feedbacksShipmentArrays));
      setAlertModalVisible(true);
      handleUpdateStatus(false);
    }
    dispatch(setAppLoader(false));
  };

  const GenerateError = (data) => {
    const columns = [
      {
        title: t('translation:default.fields.shipmentNo'),
        dataIndex: 'shipmentNo',
        key: 'shipmentNo',
        render: (text, row) => {
          return <PrimaryLabel className={`${row.status ? 'text-success' : 'text-danger'}`}>{text}</PrimaryLabel>;
        },
      },
      {
        title: t('translation:shipmentAdd.vendorDetails.statusMessage'),
        dataIndex: 'message',
        key: 'message',
        render: (text, row) => {
          return <p className={`m-0 ${row.status ? 'text-success' : 'text-danger'}`}>{text}</p>;
        },
      },
    ];
    return <SimpleTable columns={columns} data={data} />;
  };
  const handleIsAcknowledgedModalResponse = (response) => {
    setToAcknowledgedModalVisible(false);
    const errorShipmentNo = [];

    selectedShipments.forEach(({ fromShipmentStatus, shipmentNumber }) => {
      if (!SHIPMENT_STATUS_ALLOWED_STATUS_FOR_CONFIRMED.includes(fromShipmentStatus)) {
        errorShipmentNo.push(shipmentNumber);
      }
    });

    if (errorShipmentNo.length > 0) {
      setAlertModalDescription(`${errorShipmentNo.join(', ')} ${t('translation:default.notification.failed')}`);
      setAlertModalVisible(true);
    } else {
      handleModalResponse(response);
    }
  };

  const handleCancellationCostVisibility = () => {
    setIsCancelledCostVisible(true);
    setIsCancelledCostModalVisible(false);
  };

  const shipmentStatus = Form.useWatch('shipmentStatus', form);

  useEffect(() => {
    if (isShipmentCancellationCost && shipmentStatus === SHIPMENT_STATUS.Cancelled) {
      setIsCancelledCostModalVisible(true);
    } else if (isCancelledCostVisible) {
      setIsCancelledCostVisible(false);
    }
  }, [shipmentStatus]);

  const handleReasonChange = ({ label }) => {
    form.setFieldsValue({ reasonDesc: label });
  };

  return (
    <Form form={form} onFinish={onSubmit} initialValues={{}} preserve={false}>
      <ShipmentStatusSelector
        name="shipmentStatus"
        rules={[
          {
            required: true,
            message: t('translation:default.fields.status.message'),
          },
        ]}
      />

      {SHIPMENT_STATUS_FOR_REASONS.includes(shipmentStatus) && (
        <>
          <ShipmentStatusReasonSelect
            name="reasonCode"
            rules={[
              {
                required: true,
                message: t('translation:default.fields.reason.message'),
              },
            ]}
            onChange={handleReasonChange}
            margin="mb-14"
          />
          <HiddenFormInput name="reasonDesc" />
        </>
      )}

      {isCancelledCostVisible && (
        <FormInputNumber
          label="Shipment Cancellation Charges"
          editMode
          margin="mb-0"
          name="shipmentCancelCost"
          inputType="number"
          className="w-60"
        />
      )}

      <ConfirmationModal
        open={toDeliveredModalVisible}
        onClose={() => setToDeliveredModalVisible(false)}
        description={t('translation:shipmentAdd.vendorDetails.confirmationModal.updateToDelivered')}
        title={t('translation:shipmentAdd.vendorDetails.confirmationModal.deliveredConfirmation')}
        okButtonVariant="primary"
        okText={t('translation:shipmentAdd.vendorDetails.confirmationModal.okText')}
        cancelText={t('translation:default.buttons.cancel')}
        onOk={() => handleModalResponse(true)}
        onCancel={() => handleModalResponse(false)}
      />

      <ConfirmationModal
        open={toAcknowledgedModalVisible}
        onClose={() => setToAcknowledgedModalVisible(false)}
        description={t('translation:shipmentAdd.vendorDetails.confirmationModal.updateToShipmentAcknowledged')}
        title={t('translation:shipmentAdd.vendorDetails.confirmationModal.acknowledgedConfirmation')}
        okButtonVariant="primary"
        okText={t('translation:shipmentAdd.vendorDetails.confirmationModal.okText')}
        cancelText={t('translation:default.buttons.cancel')}
        onOk={() => handleIsAcknowledgedModalResponse(true)}
        onCancel={() => handleIsAcknowledgedModalResponse(false)}
      />
      <WarningModal
        open={alertModalVisible}
        description={alertModalDescription}
        onClose={() => setAlertModalVisible(false)}
        onOk={() => setAlertModalVisible(false)}
      />
      <ConfirmationModal
        open={isCancelledCostModalVisible}
        onClose={() => setIsCancelledCostModalVisible(false)}
        description="Would you like to add Cancellation Charge for this Shipment?"
        title={t('translation:default.fields.alert')}
        okButtonVariant="primary"
        okText={t('translation:default.options.yes')}
        cancelText={t('translation:default.options.no')}
        onOk={handleCancellationCostVisibility}
        onCancel={() => setIsCancelledCostModalVisible(false)}
      />
    </Form>
  );
};

export default ShipmentStatusUpdateForm;
